import React, { useState } from 'react';
import { Star, Check, Loader2, AlertCircle, X } from 'lucide-react';
import { ratingsService } from '@/services/ratings';
import { toast } from 'sonner';

interface PatientRatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: string;
  doctorName: string;
  onSuccess?: () => void;
}

export const PatientRatingModal: React.FC<PatientRatingModalProps> = ({
  isOpen,
  onClose,
  appointmentId,
  doctorName,
  onSuccess,
}) => {
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [comment, setComment] = useState<string>('');

  // Critérios detalhados (1-5 estrelas)
  const [punctuality, setPunctuality] = useState<number>(0);
  const [clarity, setClarity] = useState<number>(0);
  const [care, setCare] = useState<number>(0);
  const [prudence, setPrudence] = useState<number>(0);
  const [cordiality, setCordiality] = useState<number>(0);

  // Perguntas Sim/Não
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null);
  const [resolvedProblem, setResolvedProblem] = useState<boolean | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) {
      setError('Por favor, selecione uma nota geral (estrelas) de 1 a 5.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await ratingsService.submitRating({
        appointmentId,
        rating,
        comment: comment.trim() || undefined,
        punctualityRating: punctuality || undefined,
        clarityRating: clarity || undefined,
        careRating: care || undefined,
        prudenceRating: prudence || undefined,
        cordialityRating: cordiality || undefined,
        wouldRecommend: wouldRecommend !== null ? wouldRecommend : undefined,
        resolvedProblem: resolvedProblem !== null ? resolvedProblem : undefined,
      });

      if (response.success) {
        toast.success('Avaliação enviada com sucesso! Obrigado pelo seu feedback.');
        if (onSuccess) onSuccess();
        onClose();
      } else {
        setError(response.error || 'Erro ao enviar avaliação. Tente novamente.');
      }
    } catch (err) {
      setError('Falha de conexão com o servidor.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStars = (
    value: number,
    onChange: (val: number) => void,
    hoverVal?: number,
    setHoverVal?: (val: number) => void,
    size = 28
  ) => {
    return (
      <div className="flex items-center gap-1.5">
        {[1, 2, 3, 4, 5].map((star) => {
          const isFilled = hoverVal ? star <= hoverVal : star <= value;
          return (
            <button
              type="button"
              key={star}
              onClick={() => onChange(star)}
              onMouseEnter={() => setHoverVal && setHoverVal(star)}
              onMouseLeave={() => setHoverVal && setHoverVal(0)}
              className="text-gray-300 dark:text-neutral-700 hover:scale-110 transition-transform focus:outline-none"
            >
              <Star
                size={size}
                className={
                  isFilled
                    ? 'fill-amber-400 text-amber-400 drop-shadow-[0_0_4px_rgba(251,191,36,0.3)]'
                    : 'text-gray-300 dark:text-neutral-600'
                }
              />
            </button>
          );
        })}
      </div>
    );
  };

  const renderSubRating = (label: string, val: number, onChange: (val: number) => void) => {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 border-b border-neutral-100 dark:border-neutral-800/40">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-0">{label}</span>
        {renderStars(val, onChange, 0, undefined, 20)}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="relative bg-white dark:bg-neutral-900 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl border border-neutral-200 dark:border-neutral-800 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Avaliar Consulta</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Médico(a): {doctorName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Nota Geral */}
          <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-teal-50/50 to-blue-50/50 dark:from-teal-950/10 dark:to-blue-950/10 rounded-3xl border border-teal-100/50 dark:border-teal-900/10">
            <span className="text-sm font-bold text-teal-800 dark:text-teal-400 uppercase tracking-widest mb-3">Nota Geral</span>
            {renderStars(rating, setRating, hoverRating, setHoverRating, 36)}
            <span className="text-xs text-gray-500 mt-2 font-medium">
              {rating === 1 && 'Péssimo'}
              {rating === 2 && 'Ruim'}
              {rating === 3 && 'Regular'}
              {rating === 4 && 'Bom'}
              {rating === 5 && 'Excelente!'}
            </span>
          </div>

          {/* Critérios de Qualidade */}
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Critérios de Qualidade</h3>
            {renderSubRating('Pontualidade do atendimento', punctuality, setPunctuality)}
            {renderSubRating('Clareza nas orientações e explicações', clarity, setClarity)}
            {renderSubRating('Cuidado e atenção dispensada', care, setCare)}
            {renderSubRating('Prudência e segurança demonstrada', prudence, setPrudence)}
            {renderSubRating('Cordialidade, simpatia e empatia', cordiality, setCordiality)}
          </div>

          {/* Recomendaria / Resolveu */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-neutral-50 dark:bg-neutral-800/40 rounded-2xl border border-neutral-100 dark:border-neutral-800/60">
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 block mb-3">Recomendaria o médico?</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setWouldRecommend(true)}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all border ${
                    wouldRecommend === true
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 hover:bg-neutral-50'
                  }`}
                >
                  Sim
                </button>
                <button
                  type="button"
                  onClick={() => setWouldRecommend(false)}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all border ${
                    wouldRecommend === false
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 hover:bg-neutral-50'
                  }`}
                >
                  Não
                </button>
              </div>
            </div>

            <div className="p-4 bg-neutral-50 dark:bg-neutral-800/40 rounded-2xl border border-neutral-100 dark:border-neutral-800/60">
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 block mb-3">Ajudou no seu problema?</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setResolvedProblem(true)}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all border ${
                    resolvedProblem === true
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 hover:bg-neutral-50'
                  }`}
                >
                  Sim
                </button>
                <button
                  type="button"
                  onClick={() => setResolvedProblem(false)}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all border ${
                    resolvedProblem === false
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 hover:bg-neutral-50'
                  }`}
                >
                  Não
                </button>
              </div>
            </div>
          </div>

          {/* Comentário Opcional */}
          <div className="flex flex-col gap-2">
            <label htmlFor="comment" className="text-sm font-bold text-gray-700 dark:text-gray-300">
              Comentário (opcional)
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Fique à vontade para detalhar como foi seu atendimento..."
              rows={3}
              className="w-full px-4 py-3 rounded-2xl border border-neutral-200 dark:border-neutral-800 dark:bg-neutral-950 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 flex gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-3 px-4 border border-neutral-200 dark:border-neutral-800 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-2xl transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || rating === 0}
            className="flex-1 py-3 px-4 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-2xl transition-all shadow-md shadow-teal-600/10 hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
              </>
            ) : (
              <>
                Enviar Avaliação
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
